import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Alert, AlertTitle } from '@mui/material';
import { t } from 'i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import CustomDatePicker from '../../../../components/CustomDatePicker/CustomDatePicker';
import { getMangeDataAction, updateManageByIdAction } from '../../../../actions/mangeActionV2';
import { showSuccessToast } from '../../../../slices/toastSlice';
import { resetUpdated, resetGetManage } from '../../../../slices/manageSliceV2';

const EditJobStartDateDialog = ({ open, row, onClose, searchParams }) => {
  const dispatch = useDispatch();
  const { updateManage } = useSelector((s) => s.manageV2) || {};

  const booking = row?.booking || {};
  const job = row?.job || {};
  const lrd = booking?.latestReturnDate ? dayjs(booking.latestReturnDate) : null;

  const initialValues = useMemo(() => ({
    startDate: job?.startDate || null,
  }), [job?.startDate]);

  const validationSchema = useMemo(
    () => Yup.object({
      startDate: Yup.string()
        .required(t('Start Date is required'))
        .test(
          'before-lrd-1days',
          t('Start Date must be at least 1 day before Latest Return Date'),
          (value) => {
            if (!value || !lrd) return true; // allow if no LRD available
            const v = dayjs(value);
            if (!v.isValid()) return false;
            const diffDays = v.startOf('day').diff(lrd.startOf('day'), 'day');
            return diffDays <= -1;
          }
        ),
    }),
    [lrd]
  );

  const handleSubmit = async (values) => {
    if (!row?.job?.id) return;
    await dispatch(updateManageByIdAction({ mode: 'job', id: row.job.id, data: { startDate: values.startDate } }));
  };

  useEffect(() => {
    return () => {
      dispatch(resetUpdated());
      dispatch(resetGetManage());
      if (searchParams) {
        dispatch(getMangeDataAction({ mode: 'shipment', query: searchParams.toString() }));
      }
    };
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (updateManage?.loading === 'succeeded') {
      dispatch(showSuccessToast(t('Job start date updated successfully')));
     
      onClose && onClose();
    }
  }, [updateManage, dispatch, onClose]);


  useEffect(() => {
        return async () => {
          await dispatch(resetGetManage());
         dispatch(getMangeDataAction({ mode: 'shipment', query: searchParams.toString() }));
        }
    }, [dispatch, searchParams]);

  const handleClose = () => onClose && onClose();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('Edit Job Start Date')}</DialogTitle>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <DialogContent dividers>
              {updateManage?.hasError && (
                <Box className="errorBox" sx={{ width: '100%' }}>
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {updateManage.errorMessage ? <div>{updateManage.errorMessage}</div> : ''}
                  </Alert>
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ maxWidth: 420 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('Start Date')}</Typography>
                <CustomDatePicker name="startDate" label={t('Start Date')} />
                {touched.startDate && errors.startDate && (
                  <Typography variant="caption" color="error" display="block" mt={0.5}>{errors.startDate}</Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>{t('Cancel')}</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>{t('Save')}</Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

EditJobStartDateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default EditJobStartDateDialog;
